import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  alpha,
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Fade,
  Grid,
  IconButton,
  Pagination,
  Tooltip,
  Typography,
} from "@mui/material";
import GetAppIcon from "@mui/icons-material/GetApp";
import DeleteIcon from "@mui/icons-material/Delete";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import { useTranslation } from "react-i18next";
import { useSound } from "../../../../../context/juno/SoundContext";
import { useUnifiedAudio } from "../../../../../context/juno/UnifiedAudioContext";
import { useAlert } from "../../../../../context/AlertContext";
import { apiRequest } from "../../../../../utils/api";
import { RowBox } from "../../../../../utils/styledBox";
import { getFileDateName } from "../../../../../utils/utils";
import SoundDetailModal from "./SoundDetailModal";
import ReportGmailerrorredIcon from "@mui/icons-material/ReportGmailerrorred";
import ScrollableBox from "../../../../../components/common/ScrollableBox";

interface SoundLibraryProps {
  generationCompleted: boolean;
}

// APIレスポンスに合わせてインターフェースを定義
interface SoundItem {
  uuid: string;
  status: number;
  url: string | null;
  created_at: string;
  image_url: string | null;
  params: {
    type?: string;
    model?: string;
    prompt?: string;
    lyric?: string;
    [key: string]: unknown;
    error_code?: string;
    error_detail?: string;
  };
  is_public: boolean;
}

const SoundLibrary: React.FC<SoundLibraryProps> = ({ generationCompleted }) => {
  const { t } = useTranslation();
  const [mySounds, setMySounds] = useState<SoundItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(0); // 初期値を 0 に変更し、計算後に設定
  const { fetchSoundTrigger, setFetchSoundTrigger, setSounds, downloadSoundFile } = useSound();
  const { setAlert } = useAlert();
  const resizeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const containerRef = useRef<HTMLDivElement>(null); // ルート Box への参照

  // 統合オーディオコンテキスト
  const { playSound, currentAudioType, currentSound, isAnyPlaying } = useUnifiedAudio();
  const playingUrl = currentSound?.url || "";
  const isPlaying = currentAudioType === "music" && isAnyPlaying;

  // 削除確認ダイアログ関連
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedUuid, setSelectedUuid] = useState<string | null>(null);

  // 詳細モーダル関連
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [currentIndex, setCurrentIndex] = useState<number>(0);

  // 配列の内容を比較する関数
  const areArraysEqual = (arr1: SoundItem[], arr2: SoundItem[]) => {
    if (arr1.length !== arr2.length) return false;

    for (let i = 0; i < arr1.length; i++) {
      // 重要なプロパティだけを比較
      if (arr1[i].uuid !== arr2[i].uuid || arr1[i].url !== arr2[i].url || arr1[i].status !== arr2[i].status) {
        return false;
      }
    }
    return true;
  };

  // コンテナの実際の高さに基づいてカード数を計算
  const calculateCardCount = useCallback(() => {
    if (containerRef.current) {
      const containerHeight = containerRef.current.clientHeight - 64; // ページネーション高さを引く
      const cardHeight = 176; // カード高さ + spacing
      const possibleRows = Math.max(1, Math.floor(containerHeight / cardHeight)); // 最低1行
      const totalCards = Math.max(3, possibleRows * 3); // 最低3枚
      return totalCards;
    }
    return 0; // ref がない場合は 0 を返す
  }, []); // pageSize を依存配列から削除し、常に現在の高さを計算

  // 自分の音声を取得（useCallbackでメモ化）
  const fetchSounds = useCallback(
    async (page = 1, sizeToFetch: number) => {
      // 引数で pageSize を受け取る
      // sizeToFetch が 0 以下なら何もしない
      if (sizeToFetch <= 0) return;
      setLoading(true);
      try {
        // 引数の sizeToFetch を使用
        // 重複する response 宣言を削除
        const response = await apiRequest(`/api/v1/juno/sound/list?page=${page}&page_size=${sizeToFetch}`, "GET");
        if (response.status === 200) {
          // 新しいレスポンス形式に対応
          if (response.data.status === "success" && Array.isArray(response.data.sounds)) {
            // ページネーション情報を設定
            setTotalPages(response.data.total_pages || 1);
            setCurrentPage(response.data.current_page || 1);

            // APIから直接音声データを使用
            const sounds = response.data.sounds;

            // 配列の内容を比較して、実際に変更があった場合のみ状態を更新
            if (!areArraysEqual(mySounds, sounds)) {
              setMySounds(sounds);
              // SoundContextに音声リストも設定（波形プレーヤーのため）
              setSounds(sounds);
            }
          } else {
            console.error("Unexpected API response format:", response.data);
            setMySounds([]);
            setSounds([]);
          }
        }
      } catch (error) {
        console.error("Failed to fetch sounds:", error);
        setMySounds([]);
        setSounds([]);
      } finally {
        setLoading(false);
      }
    },
    [mySounds, setSounds] // pageSize を依存配列から削除
  );

  // リサイズ時にページサイズを再計算（デバウンス処理付き）
  useLayoutEffect(() => {
    const updatePageSize = () => {
      // 前回のタイムアウトをクリア
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }

      // 300ms後に実行（デバウンス処理）
      resizeTimeoutRef.current = setTimeout(() => {
        const newSize = calculateCardCount();
        if (newSize > 0 && newSize !== pageSize) {
          setPageSize(newSize);
          // ページサイズ変更時は1ページ目から表示、ただし currentPage が 1 でない場合のみ
          if (currentPage !== 1) {
            setCurrentPage(1);
          }
        }
      }, 300);
    };

    // 初期化時に一度実行
    updatePageSize();

    // リサイズイベントリスナーを追加
    window.addEventListener("resize", updatePageSize);

    // クリーンアップ関数
    return () => {
      window.removeEventListener("resize", updatePageSize);
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
    };
  }, [calculateCardCount, pageSize]);

  // 削除確認ダイアログを開く
  const handleDialogOpen = (uuid: string) => {
    setSelectedUuid(uuid);
    setDialogOpen(true);
  };

  // 削除確認後、実際に削除を実行
  const handleConfirmedDelete = async (uuid: string) => {
    await handleDelete(uuid);
    setDialogOpen(false);
  };

  // 音声を削除
  const handleDelete = async (uuid: string) => {
    try {
      const response = await apiRequest(`/api/v1/juno/sound/${uuid}`, "DELETE");
      if (response.status === 200) {
        // 削除成功時の処理
        setMySounds((prev) => prev.filter((sound) => sound.uuid !== uuid));
        setAlert("success", t("juno.sound.message.deleted"));
        // モーダルが開いていれば閉じる
        if (openModal) {
          setOpenModal(false);
        }
      }
    } catch (error) {
      console.error("Failed to delete sound:", error);
    }
  };

  // 音声をダウンロード - APIからBlobを取得してダウンロード
  const handleDownload = (uuid: string) => {
    // useJunoMethodsと同様のファイル命名規則を使用
    const filename = `juno-${getFileDateName()}.mp3`;
    // SoundContextのダウンロード機能を使用
    downloadSoundFile(uuid, filename);
  };

  // 詳細モーダルを開く
  const handleOpenModal = (index: number) => {
    setCurrentIndex(index);
    setOpenModal(true);
  };

  // 詳細モーダルを閉じる
  const handleCloseModal = () => {
    setOpenModal(false);
  };

  // 次の音声へ
  const handleNextSound = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % mySounds.length);
  };

  // 前の音声へ
  const handlePrevSound = () => {
    setCurrentIndex((prevIndex) => (prevIndex - 1 + mySounds.length) % mySounds.length);
  };

  // 音声を選択して再生 - 統合コンテキスト使用
  const handleSelect = (url: string | null) => {
    if (url) {
      // 統合コンテキスト経由で再生（他の種類のオーディオは自動停止）
      playSound(url);
    }
  };

  // 処理中のサウンドがあるかをメモ化
  const hasProcessingSounds = useMemo(() => {
    return mySounds.some((sound) => sound.status === 3); // Processing status = 3
  }, [mySounds]);

  // 最初のレンダリング時、トリガー、ページ、ページサイズ変更時に音声を取得
  useEffect(() => {
    // pageSize が計算された後（0より大きい）に fetchSounds を呼び出す
    if (pageSize > 0) {
      fetchSounds(currentPage, pageSize); // pageSize を渡す
    }
    if (fetchSoundTrigger) {
      setFetchSoundTrigger(false);
    }
    // 依存配列から fetchSounds を削除（useCallback でメモ化されているが、pageSize変更で再生成されるため）
  }, [fetchSoundTrigger, generationCompleted, currentPage, pageSize, setFetchSoundTrigger]);

  // 処理中のサウンドがある場合、10秒ごとにポーリングしてステータスを更新
  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (hasProcessingSounds && pageSize > 0) {
      // pageSize > 0 を確認
      interval = setInterval(async () => {
        await fetchSounds(currentPage, pageSize); // pageSize を渡す
      }, 10000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
    // 依存配列から fetchSounds を削除
  }, [hasProcessingSounds, currentPage, pageSize]);

  // ページ変更ハンドラ
  const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
  };

  const renderSoundItem = (sound: SoundItem, index: number) => {
    // エラー状態の判定（status=2）
    const isError = sound.status === 2;
    // 処理中の判定（status=3）
    const isProcessing = sound.status === 3;

    // パラメータから必要な情報を抽出
    const prompt = sound.params?.prompt || "";
    const model = sound.params?.model || "";
    const lyric = sound.params?.lyric || "";

    return (
      <Fade in={true} key={sound.uuid} timeout={(index + 1) * 200 > 1500 ? 1000 : index * 200}>
        {/* 音声カード */}
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 4,
          }}
        >
          <Card
            variant="outlined"
            sx={{
              height: "100%",
              display: "flex",
              width: "100%",
              minHeight: "160px",
              cursor: !isProcessing ? "pointer" : "default",
              "&:hover": {
                boxShadow: !isProcessing ? 3 : 0,
              },
              maxHeight: "160px",
            }}
            onClick={!isProcessing ? () => handleOpenModal(index) : undefined}
          >
            {sound.image_url && (
              <Box sx={{ flexShrink: 0 }}>
                <CardMedia
                  component="img"
                  sx={{
                    width: "160px",
                    height: "100%",
                    objectFit: "cover",
                  }}
                  image={sound.image_url}
                  alt={prompt}
                />
              </Box>
            )}
            <CardContent
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                flexGrow: 1,
                overflow: "hidden",
              }}
            >
              <Typography
                variant="subtitle1"
                gutterBottom
                noWrap
                title={isError ? lyric : prompt}
                sx={{
                  maxWidth: "100%",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  mb: 0.5,
                }}
              >
                {lyric || prompt}
              </Typography>
              <RowBox
                justifyContent={"space-between"}
                sx={{
                  flexWrap: "wrap",
                  gap: 0.5,
                }}
              >
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {model ? t(`juno.sound.ai.${model.toLowerCase()}.title`) : ""}
                </Typography>
                {/* エラー状態の表示 */}
                {isError && (
                  <Tooltip title={t("juno.sound.error.generate")}>
                    <Button
                      variant="text"
                      color="error"
                      size="small"
                      startIcon={<ReportGmailerrorredIcon fontSize="small" />}
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      {t("juno.sound.message.error")}
                    </Button>
                  </Tooltip>
                )}
                <Typography variant="caption" color="text.secondary" display="block">
                  {new Date(sound.created_at).toLocaleString()}
                </Typography>
              </RowBox>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mt: "auto",
                  pt: 1,
                  flexWrap: "wrap",
                  gap: 1,
                }}
              >
                <Box sx={{ display: "flex", gap: 1 }}>
                  {isProcessing || isError ? (
                    <IconButton size="small" disabled={true}>
                      <GetAppIcon fontSize="small" />
                    </IconButton>
                  ) : (
                    <Tooltip title={t("juno.sound.soundMenu.download")}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation(); // カードクリックイベントの伝播を止める
                            handleDownload(sound.uuid);
                          }}
                        >
                          <GetAppIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}

                  {isProcessing || isError ? (
                    <IconButton
                      size="small"
                      disabled={true}
                      color={playingUrl === sound.url && isPlaying ? "primary" : "default"}
                    >
                      {playingUrl === sound.url && isPlaying ? (
                        <PauseIcon fontSize="small" />
                      ) : (
                        <PlayArrowIcon fontSize="small" />
                      )}
                    </IconButton>
                  ) : (
                    <Tooltip title={playingUrl === sound.url && isPlaying ? t("common.stop") : t("common.play")}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation(); // カードクリックイベントの伝播を止める
                            handleSelect(sound.url);
                          }}
                          color={playingUrl === sound.url && isPlaying ? "primary" : "default"}
                        >
                          {playingUrl === sound.url && isPlaying ? (
                            <PauseIcon fontSize="small" />
                          ) : (
                            <PlayArrowIcon fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  {isProcessing ? (
                    <IconButton size="small" disabled={true}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  ) : (
                    <Tooltip title={t("juno.sound.soundMenu.delete")}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation(); // カードクリックイベントの伝播を止める
                            handleDialogOpen(sound.uuid);
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}

                  {isProcessing && <CircularProgress size={20} sx={{ ml: 1 }} />}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Fade>
    );
  };

  return (
    // ルート Box に ref を設定し、高さを 100% にして親から継承
    <Box
      ref={containerRef}
      sx={{
        borderRadius: 1,
        backgroundColor: (theme) => alpha(theme.palette.background.custom1, 0.5),
        backdropFilter: "blur(10px)",
        display: "flex",
        flexDirection: "column",
        width: "100%",
        p: 2,
        height: "100%",
        overflow: "hidden",
      }}
    >
      {mySounds.length === 0 ? (
        loading ? (
          <Box
            sx={{
              justifyContent: "center",
              display: "flex",
              width: "100%",
              height: "100%",
              alignItems: "center",
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <Box
            sx={{
              justifyContent: "center",
              display: "flex",
              width: "100%",
              height: "100%",
              alignItems: "center",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              No sounds available.
            </Typography>
          </Box>
        )
      ) : (
        <>
          {/* ScrollableBox の sx から Grid 関連のスタイルを削除 */}
          <ScrollableBox sx={{ flexGrow: 1 /* 他のスタイルは削除 */ }}>
            {/* Grid container に alignContent/alignItems を設定 */}
            <Grid container spacing={2} sx={{ alignContent: "flex-start", alignItems: "flex-start" }}>
              {mySounds.map((sound, index) => renderSoundItem(sound, index))}
            </Grid>
          </ScrollableBox>

          {/* ページネーションコントロール */}
          {totalPages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2, flexShrink: 0 }}>
              <Pagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </>
      )}
      {/* </ > 不要な閉じタグ */}

      {/* 詳細モーダル */}
      <SoundDetailModal
        open={openModal}
        onClose={handleCloseModal}
        sounds={mySounds}
        currentIndex={currentIndex}
        handleNextSound={handleNextSound}
        handlePrevSound={handlePrevSound}
        handleOpenDeleteDialog={handleDialogOpen}
        handleSoundDownload={handleDownload}
        playingUrl={playingUrl}
        isPlaying={isPlaying}
        handleSelect={handleSelect}
      />

      {/* 削除確認ダイアログ */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} disableEnforceFocus>
        <DialogTitle>{t("juno.history.delete.title")}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t("juno.history.delete.description")}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button disableElevation onClick={() => setDialogOpen(false)} color="primary">
            {t("common.cancel")}
          </Button>
          <Button
            onClick={() => {
              if (selectedUuid) {
                handleConfirmedDelete(selectedUuid);
              }
            }}
            color="error"
          >
            {t("common.delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SoundLibrary;
